<template>
  <div class="px-4 sm:px-6 lg:px-8 pb-4 h-screen">
    <!-- Search and Filter Bar -->
    <div class="sm:flex sm:items-center mb-6">
      <div class="sm:flex-auto pt-10">
        <h1 class="text-2xl font-semibold text-gray-900">Inventory Items</h1>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search items..."
          class="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div
            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg"
          >
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    v-for="header in tableHeaders"
                    :key="header.key"
                    scope="col"
                    class="py-3.5 pl-3 pr-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {{ header.label }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr
                  v-for="item in filteredItems"
                  :key="item.id"
                  class="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td
                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                  >
                    {{ item.number }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ item.name }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ formatDate(item.expiry) }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ item.lot_no }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      :class="{
                        'px-2 py-1 rounded-full text-xs font-medium': true,
                        'bg-yellow-100 text-yellow-800':
                          item.stock < item.minQty,
                        'bg-green-100 text-green-800':
                          item.stock >= item.minQty,
                      }"
                    >
                      {{ item.stock }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ item.minQty }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ item.maxQty }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ item.serial_no }}
                  </td>
                </tr>
                <tr v-if="filteredItems.length === 0">
                  <td
                    :colspan="tableHeaders.length"
                    class="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No items found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      class="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6"
    >
      <div class="flex flex-1 justify-between sm:hidden">
        <button
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Previous
        </button>
        <button
          @click="currentPage++"
          :disabled="currentPage * itemsPerPage >= items.length"
          class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{
              (currentPage - 1) * itemsPerPage + 1
            }}</span>
            to
            <span class="font-medium">{{
              Math.min(currentPage * itemsPerPage, items.length)
            }}</span>
            of <span class="font-medium">{{ items.length }}</span> results
          </p>
        </div>
        <div>
          <nav
            class="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
            >
              <span class="sr-only">Previous</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
            </button>
            <template v-for="page in visiblePages" :key="page">
              <button
                v-if="page === '...'"
                disabled
                class="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700"
              >
                {{ page }}
              </button>
              <button
                v-else
                @click="currentPage = page"
                :class="{
                  'relative z-10 inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20': true,
                  'bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600':
                    currentPage === page,
                  'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0':
                    currentPage !== page,
                }"
              >
                {{ page }}
              </button>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage * itemsPerPage >= items.length"
              class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              :class="{
                'opacity-50 cursor-not-allowed':
                  currentPage * itemsPerPage >= items.length,
              }"
            >
              <span class="sr-only">Next</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { notify } from "@kyvg/vue3-notification";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";

export default {
  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10,
      tableHeaders: [
        { key: "number", label: "Number" },
        { key: "name", label: "Name" },
        { key: "expiry", label: "Expiry" },
        { key: "lot_no", label: "Lot No" },
        { key: "stock", label: "Stock" },
        { key: "minQty", label: "Min Qty" },
        { key: "maxQty", label: "Max Qty" },
        { key: "serial_no", label: "Serial No" },
      ],
      items: [], // This will be populated in created/mounted
    };
  },
  computed: {
    filteredItems() {
      let result = this.items;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (item) =>
            item.name.toLowerCase().includes(query) ||
            item.number.toString().includes(query) ||
            item.lot_no?.toLowerCase().includes(query) ||
            item.serial_no?.toLowerCase().includes(query)
        );
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return result.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.items.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      // Always show first page
      pages.push(1);

      // Add ellipsis if needed
      if (current > 3) {
        pages.push("...");
      }

      // Add current page and next two pages
      for (
        let i = Math.max(2, current - 1);
        i <= Math.min(current + 2, total - 1);
        i++
      ) {
        pages.push(i);
      }

      // Add last page if not already included
      if (total > 1 && !pages.includes(total)) {
        if (current < total - 3) {
          pages.push("...");
        }
        pages.push(total);
      }

      return pages;
    },
  },
  created() {
    this.fetchItems();
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    async fetchItems() {
      const token = localStorage.getItem("token");
      if (!token) {
        // Handle case when user is not authenticated
        this.$router.push("/login");
        return;
      }

      const AuthStr = "Token ".concat(token);
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}search_item/?keyword=`,
          {
            headers: {
              Authorization: AuthStr,
              "Content-Type": "application/json",
            },
            timeout: 10000, // 10 second timeout
          }
        );

        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data.items)
        ) {
          this.items = response.data.data.items;
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        let errorMessage = "Error loading items";

        if (error.response) {
          if (error.response.status === 401) {
            // Unauthorized - redirect to login
            this.$router.push("/login");
            return;
          }
          errorMessage =
            error.response.data.detail ||
            error.response.data.message ||
            errorMessage;
        } else if (error.request) {
          errorMessage =
            "No response from server. Please check your connection.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        notify({
          title: errorMessage,
          type: "error",
          duration: 5000,
        });
      }
    },
  },
};
</script>
